import MixpanelPluginV2 from '@satellite/plugins/MixpanelPluginV2.js';
import { useAnalytics, type AnalyticsClient } from '@satellite/composables/useAnalytics';
import {
  getLunaMixpanelInstance,
  registerLunaMixpanelInstance
} from '@/analytics/mixpanelInstance';
import config from '@/config/config';

/** The subset of `Auth/me` the analytics identity uses. */
export interface LunaAnalyticsUser {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  orgId?: string | null;
  orgName?: string | null;
  orgType?: string | null;
  orgCreateDateTime?: string | null;
  tcConfirmedAt?: string | null;
}

/**
 * Builds the Mixpanel client Luna provides through `analyticsKey`.
 *
 * No `sessionReplay` option is passed to `init()`: Luna records the registration funnel by
 * hand (see useLunaSessionRecording) rather than sampling sessions, which is also what the
 * legacy plugin did.
 */
export function createLunaAnalyticsClient(): AnalyticsClient {
  const mixpanel = new MixpanelPluginV2({
    token: config.mixpanel_token ?? null,
    enabled: Boolean(config.mixpanel_token),
    debug: config.server_env === 'development'
  });
  mixpanel.init();
  registerLunaMixpanelInstance(mixpanel);

  return {
    // No-payload events arrive here without a second argument; `{}` is what the legacy
    // plugin sent for those too.
    track: (event, payload) => mixpanel.track(String(event), payload ?? {}),
    identify: value => {
      if (typeof value !== 'string' || !value) {
        return;
      }
      mixpanel.identify(value);
    },
    reset: () => mixpanel.reset(),
    registerSuper: properties => mixpanel.setProperties(properties),
    setUserProperties: properties => mixpanel.setUserProperties(properties)
  };
}

/**
 * Identity side of Luna's analytics.
 *
 * Unlike Helios and Circe, this is not driven by a watcher on the auth store: Luna
 * identifies the carrier right after `POST /carrier/register`, while the account is still
 * unverified and `Auth.me` is null, so a store watcher would miss that call. The three
 * identity points call `identifyLunaUser` explicitly instead.
 */
export function useLunaAnalytics() {
  const analytics = useAnalytics();

  function identifyLunaUser(me: LunaAnalyticsUser | null | undefined): void {
    if (!me?.email) {
      return;
    }

    /**
     * distinct_id stays the user's email (not user.id) to preserve continuity with the
     * legacy MixpanelPlugin's Mixpanel cohorts/history for existing Luna carriers.
     */
    analytics.identify(me.email);

    analytics.setUserProperties({
      $name: `${me.firstName ?? ''} ${me.lastName ?? ''}`,
      $email: me.email,
      'Nova User ID': me.id ?? '',
      'User Role': me.role ?? '',
      'Org ID': me.orgId ?? ''
    });

    if (me.orgId) {
      // Group properties have no equivalent on the shared AnalyticsClient contract, so this
      // goes straight to the plugin instance (see analytics/mixpanelInstance.ts).
      getLunaMixpanelInstance().setGroupProperties('Org ID', me.orgId, {
        Name: me.orgName ?? '',
        Type: me.orgType ?? '',
        'Created At': me.orgCreateDateTime ?? '',
        'TC Confirmed At': me.tcConfirmedAt ?? ''
      });
    }

    analytics.registerSuper({
      org_id: me.orgId ?? '',
      org_name: me.orgName ?? ''
    });
  }

  return { identifyLunaUser };
}
